<script setup lang="ts">
import type {
  CmsElementProductListing,
  CmsElementSidebarFilter,
} from "@shopware/composables";
import { useCmsTranslations } from "@shopware/composables";
import { defu } from "defu";
import { computed, provide, reactive, ref, useTemplateRef } from "vue";
import type { ComputedRef, UnwrapNestedRefs } from "vue";
import { type LocationQueryRaw, useRoute, useRouter } from "vue-router";
import { useCategoryListing } from "#imports";
import type { Schemas, operations } from "#shopware";
import { useSidebarStore } from "@/stores/sidebar";

const props = defineProps<{
  content: CmsElementProductListing | CmsElementSidebarFilter;
  listingType?: string;
}>();

type Translations = {
  listing: {
    filters: string;
    sort: string;
    resetFilters: string;
  };
};

let translations: Translations = {
  listing: {
    filters: "Filters",
    sort: "Sort",
    resetFilters: "Reset filters",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const route = useRoute();
const router = useRouter();

const {
  changeCurrentSortingOrder,
  filtersToQuery,
  getCurrentFilters,
  getCurrentSortingOrder,
  getInitialFilters,
  getSortingOrders,
  getTotal,
  search,
  loading
} = useCategoryListing();

const sidebarSelectedFilters: UnwrapNestedRefs<{
  [key: string]: Set<string> | string | number | boolean | undefined;
}> = reactive<{
  manufacturer: Set<string>;
  properties: Set<string>;
  "min-price": string | number | undefined;
  "max-price": string | number | undefined;
  rating: string | number | undefined;
  "shipping-free": boolean | undefined;
}>({
  manufacturer: new Set(),
  properties: new Set(),
  "min-price": undefined,
  "max-price": undefined,
  rating: undefined,
  "shipping-free": undefined,
});

const searchCriteriaForRequest: ComputedRef<Schemas["ProductListingCriteria"]> =
  computed(() => ({
    manufacturer: [
      ...(sidebarSelectedFilters.manufacturer as Set<string>),
    ]?.join("|"),
    properties: [...(sidebarSelectedFilters.properties as Set<string>)]?.join(
      "|",
    ),
    "min-price": sidebarSelectedFilters["min-price"] as number,
    "max-price": sidebarSelectedFilters["max-price"] as number,
    order: getCurrentSortingOrder.value as string,
    "shipping-free": sidebarSelectedFilters["shipping-free"] as boolean,
    rating: sidebarSelectedFilters.rating as number,
    search: "",
    limit: route.query.limit ? Number(route.query.limit) : 15,
  }));

for (const param in route.query) {
  if (Object.prototype.hasOwnProperty.call(sidebarSelectedFilters, param)) {
    if (
      sidebarSelectedFilters[param] &&
      typeof sidebarSelectedFilters[param] === "object"
    ) {
      const elements = (route.query[param] as unknown as string).split("|");
      for (const element of elements) {
        sidebarSelectedFilters[param].add(element);
      }
    } else {
      const queryValue = route.query[param];
      if (queryValue !== null && !Array.isArray(queryValue)) {
        sidebarSelectedFilters[param] = queryValue;
      }
    }
  }
}

const onOptionSelectToggle = async ({
  code,
  value,
}: {
  code: string;
  value: string | number | boolean;
}) => {
  scrollToListingHeader();

  if (!["properties", "manufacturer"].includes(code)) {
    sidebarSelectedFilters[code] = value;
  } else {
    const filterSet = sidebarSelectedFilters[code] as Set<string>;
    const stringValue = String(value);
    if (filterSet?.has(stringValue)) {
      filterSet.delete(stringValue);
    } else {
      filterSet?.add(stringValue);
    }
  }

  await executeSearch();
};

const executeSearch = async () => {
  await search(searchCriteriaForRequest.value);
  const query = filtersToQuery(searchCriteriaForRequest.value);
  const { limit: _, ...queryWithoutLimit } = query; // remove limit from query
  await router.push({
    query: queryWithoutLimit as LocationQueryRaw,
  });
};

const clearFilters = () => {
  (sidebarSelectedFilters.manufacturer as Set<string>).clear();
  (sidebarSelectedFilters.properties as Set<string>).clear();
  sidebarSelectedFilters["min-price"] = undefined;
  sidebarSelectedFilters["max-price"] = undefined;
  sidebarSelectedFilters.rating = undefined;
  sidebarSelectedFilters["shipping-free"] = undefined;
};

const selectedOptionIds = computed(() => [
  ...(sidebarSelectedFilters.properties as Set<string>),
  ...(sidebarSelectedFilters.manufacturer as Set<string>),
]);
provide("selectedOptionIds", selectedOptionIds);


const removeFilter = async (filter: string | number | boolean) => {
  if (["manufacturer", "properties"].includes(filter as string)) {
    (sidebarSelectedFilters[filter as keyof typeof sidebarSelectedFilters] as Set<string>).delete(filter as string);
  } else {
    sidebarSelectedFilters[filter as keyof typeof sidebarSelectedFilters] = undefined;
  }

  await executeSearch();
};

const selectedFilters = computed(() => {
  return [
    ...(sidebarSelectedFilters.manufacturer as Set<string>).values(),
    ...(sidebarSelectedFilters.properties as Set<string>).values(),
  ].map((id) => {
    // Check if it's a manufacturer filter
    const manufacturer = getInitialFilters.value
      .find(f => f.code === "manufacturer")
      ?.entities.find(e => e.id === id);

    if (manufacturer) return { id, name: manufacturer.name, type: "manufacturer" };

    // Check if it's a property filter
    const propertyOption = getInitialFilters.value
      .filter(f => f.code === "properties") // Get all properties
      .flatMap(f => f.options || []) // Flatten to options array
      .find(o => o.id === id);

    if (propertyOption) return { id, name: propertyOption.name, type: "properties" };

    // Default: Return ID if no match is found
    return { id, name: id, type: "unknown" };
  });
});

function scrollToListingHeader() {
  const listingHeaderSection = document.querySelector('.product-listing-section');
  if (listingHeaderSection) {
    listingHeaderSection.scrollIntoView({ behavior: 'smooth' });
  }
}

const removeFilterId = async (filterId: string, filterType: "manufacturer" | "properties") => {
  scrollToListingHeader();

  if (filterType === "manufacturer") {
    sidebarSelectedFilters.manufacturer.delete(filterId);
  } else if (filterType === "properties") {
    sidebarSelectedFilters.properties.delete(filterId);
  }

  await executeSearch();
};

const removePriceFilters = () => {
  removeFilter('max-price')
  removeFilter('min-price')
}

const sidebarStore = useSidebarStore();

const closeSidebarWithFilters = () => {
  sidebarStore.closeSidebar(); 
}

const closeSidebarWithOutFilters = () => {
  sidebarStore.closeSidebar(); 
  clearFilters()
}

// Accordion state: track which filter is currently open
const openFilterId = ref<string | null>(null);
const productFilterSection = ref<HTMLElement | null>(null);

const toggleFilter = (filterId: string, event?: Event) => {
  // Store the previous state to check if any filter was open
  const wasAnyFilterOpen = openFilterId.value !== null;
  
  // If clicking the same filter, close it; otherwise, open the new one
  const isOpening = openFilterId.value !== filterId;
  openFilterId.value = isOpening ? filterId : null;
  
  // Only scroll if we're opening a filter AND no filter was previously open
  if (isOpening && !wasAnyFilterOpen) {
    // First, check if filter section is already visible/sticky
    nextTick(() => {
      const filterSection = document.querySelector('.sidebar-filter');
      if (filterSection) {
        const rect = filterSection.getBoundingClientRect();
        
        // Check if filter is already in the viewport and near the top (sticky position)
        // If top is between -50 and 150, it's already visible/sticky
        const isAlreadyVisible = rect.top >= -50 && rect.top <= 150;
        
        // Only scroll if the filter is not already visible
        if (!isAlreadyVisible) {
          // Use a larger offset on very large (IMAX-like) screens
          const isImaxScreen = window.innerWidth >= 1920;
          const yOffset = isImaxScreen ? -220 : -20;
          const y = rect.top + window.scrollY + yOffset;

          window.scrollTo({
            top: y,
            behavior: "smooth",
          });
        }
      }
    });
  }
  
  // Scroll within the filter container to show the expanded content
  if (isOpening) {
    nextTick(() => {
      // Find the button element that was clicked
      const targetButton = event?.target as HTMLElement;
      if (targetButton) {
        // Find the parent filter container (.collapse-item)
        const filterContainer = targetButton.closest('.collapse-item') as HTMLElement;
        if (filterContainer) {
          // Wait for the dropdown animation to complete (300ms transition)
          setTimeout(() => {
            // Find the scrollable container (the parent .sidebar-filter element)
            const scrollableContainer = filterContainer.closest('.sidebar-filter') as HTMLElement;
            if (scrollableContainer) {
              // Get the dimensions and positions
              const containerRect = scrollableContainer.getBoundingClientRect();
              const filterRect = filterContainer.getBoundingClientRect();
              
              // Calculate the top position relative to the container
              const relativeTop = filterRect.top - containerRect.top;
              const currentScroll = scrollableContainer.scrollTop;
              
              // Get the height of the filter including the expanded content
              const filterHeight = filterContainer.offsetHeight;
              
              // Calculate available viewport height (container height minus header offset)
              const headerOffset = 120; // Offset for sticky header and padding
              const availableHeight = containerRect.height - headerOffset;
              
              // Calculate target scroll position
              let targetScroll;
              
              if (filterHeight > availableHeight) {
                // If content is taller than viewport, just scroll to top of filter
                targetScroll = currentScroll + relativeTop - 100;
              } else {
                // If content fits, ensure the entire filter is visible
                const filterBottom = relativeTop + filterHeight;
                const viewportBottom = availableHeight;
                
                if (filterBottom > viewportBottom) {
                  // Filter extends below viewport, scroll to show entire filter
                  targetScroll = currentScroll + (filterBottom - viewportBottom) + 20;
                } else {
                  // Filter is already visible, just adjust to top
                  targetScroll = currentScroll + relativeTop - 100;
                }
              }
              
              scrollableContainer.scrollTo({
                top: Math.max(0, targetScroll),
                behavior: "smooth",
              });
            }
          }, 350); // Wait for animation to complete
        }
      }
    });
  }
}

const isFilterOpen = (filterId: string) => {
  return openFilterId.value === filterId;
}
</script>
<template>
  <div
    ref="productFilterSection"
    class="sidebar-filter-inner"
  >

    <!-- Filter header -->
    <div
      class="px-5 lg:px-0 py-4 lg:py-0 sticky top-0 z-10 border-0 border-b lg:border-0 bg-white border-grey-100">
      <div class="flex justify-between items-center lg:pb-5">
        <div class="title">
          <h2 class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none">{{ $t('listing.filters') }}</h2>
        </div>
        <button class="text-xl block lg:hidden" :aria-label="$t('productListing.ariaLabelCloseMenu')" @click="closeSidebarWithOutFilters" id="closeSidebar">
          <i class="icon-close-outline" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div class="content relative mt-0 lg:mt-6 pb-[120px] lg:pb-0">
      <!-- Filters content inner -->
      <div class="px-5 lg:px-0 md:min-h-[500px]">
        <ul
          v-if="selectedFilters.length > 0 || sidebarSelectedFilters['shipping-free']
            || sidebarSelectedFilters['rating'] || sidebarSelectedFilters['max-price'] || sidebarSelectedFilters['min-price']"
          class="flex flex-wrap mb-4 list-none"
        >

            <li
              v-for="filter in selectedFilters"
              :key="filter.id"
              class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]"
            >
              <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">
                {{ filter.name }}
              </span>
              <button 
                class="text-purple hover:text-red ml-2.5" 
                :aria-label="$t('productListing.ariaLabelRemoveFilter')"
                @click="removeFilterId(filter.id, filter.type)"
              >
                <i class="icon-close-outline font-bold text-xs"></i>
              </button>
            </li>

          <li v-if="sidebarSelectedFilters['shipping-free']" class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]">
            <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">{{ $t("filters.freeShipping") }}</span>
            <button @click="removeFilter('shipping-free')" class="text-purple hover:text-red ml-2.5" :aria-label="$t('productListing.ariaLabelRemoveFilter')">
              <i class="icon-close-outline font-bold text-xs"></i>
            </button>
          </li>

          <li v-if="sidebarSelectedFilters['rating']" class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]">
            <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">{{ $t("filters.rating") }}: {{ sidebarSelectedFilters['rating'] }} <i class="icon-star-sharp text-rating-yellow"></i></span>
            <button @click="removeFilter('rating')" class="text-purple hover:text-red ml-2.5" :aria-label="$t('productListing.ariaLabelRemoveFilter')">
              <i class="icon-close-outline font-bold text-xs"></i>
            </button>
          </li>

          <li v-if="sidebarSelectedFilters['max-price'] && sidebarSelectedFilters['min-price']" class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]">
            <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">{{ $t("filters.price") }}: {{ sidebarSelectedFilters['min-price'] }} - {{ sidebarSelectedFilters['max-price'] }}</span>
            <button class="text-purple hover:text-red ml-2.5" @click="removePriceFilters()" :aria-label="$t('productListing.ariaLabelRemoveFilter')">
              <i class="icon-close-outline font-bold text-xs"></i>
            </button>
          </li>

          <li v-if="sidebarSelectedFilters['max-price'] && !sidebarSelectedFilters['min-price']" class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]">
            <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">{{ $t("filters.maxPrice") }}: {{ sidebarSelectedFilters['max-price'] }}</span>
            <button class="text-purple hover:text-red ml-2.5" @click="removeFilter('max-price')" :aria-label="$t('productListing.ariaLabelRemoveFilter')">
              <i class="icon-close-outline font-bold text-xs"></i>
            </button>
          </li>

          <li v-if="!sidebarSelectedFilters['max-price'] && sidebarSelectedFilters['min-price']" class="bg-light-grey rounded-lg py-2.5 px-3.5 inline-flex items-center mb-2 mr-2 min-h-[36px]">
            <span class="font-mulish font-normal text-sm 3xl:text-base text-grey-700">{{ $t("filters.minPrice") }}: {{ sidebarSelectedFilters['min-price'] }}</span>
            <button class="text-purple hover:text-red ml-2.5" @click="removeFilter('min-price')" :aria-label="$t('productListing.ariaLabelRemoveFilter')">
              <i class="icon-close-outline font-bold text-xs"></i>
            </button>
          </li>

        </ul>

        <ClientOnly>
          <div class="accordion sidebar-accordion order-2 xl:order-1">

            <div
              v-for="filter in getInitialFilters"
              :key="`${filter?.id || filter?.code}`"
            >
              <ListingFiltersSwProductListingFilter
                @select-filter-value="onOptionSelectToggle"
                :selected-filters="getCurrentFilters"
                :filter="filter"
                :is-filter-visible="isFilterOpen(filter?.id || filter?.code)"
                @toggle="(event) => toggleFilter(filter?.id || filter?.code, event)"
              />
            </div>
          </div>
        </ClientOnly>
      </div>
    </div>

    <div
      class=" transform translate-y-0 flex lg:hidden justify-between items-center bg-primary-yellow-400 px-5 lg:px-0 py-3.5 sticky w-full bottom-0 z-40 transition-all duration-300 ease-in"
      
    >
      <div class="absolute top-50 transition-opacity duration-300 ease-in-out" :class="loading ? '' : 'opacity-0'" role="status">
        <svg aria-hidden="true" class="inline w-4 h-4 text-gray-200 animate-spin dark:text-white/30   fill-white" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
        </svg>
        <span class="sr-only">{{ $t('search.loading') }}</span>
      </div>
      <span :class="loading ? ' opacity-0' : ''" class="text-white font-mulish font-medium text-sm transition-all duration-150 ease-in">
        <ClientOnly>
          {{ getTotal }} {{ $t('productListing.showProducts') }}
        </ClientOnly>
      </span>
      <button class="btn btn-secondary" @click="closeSidebarWithFilters">{{ $t('productListing.apply') }}</button>
    </div>
  </div>
</template>

<style scoped>
.sidebar-filter .sidebar-accordion>div:first-child {
    @apply lg:border-t border-gray-200;
}
</style>
